'use client';

import React from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useCategoryStore } from '@/stores/categoryStore';

const PlanetScene = dynamic(
  () =>
    import('@/components/three/PlanetScene').then((mod) => ({
      default: mod.PlanetScene,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-[320px] sm:h-[450px] flex items-center justify-center bg-[#0A0A12] text-emerald-400 font-mono text-xs">
        Cargando Planeta Kaio...
      </div>
    ),
  }
);

export interface CategoryItem {
  id: number;
  name: string;
  slug: string;
  description: string;
  icon: string;
  dragonBallStar: number;
  planetRotY: number;
  planetRotX: number;
  color: string;
}

interface HeroSectionProps {
  categories: CategoryItem[];
}

export function HeroSection({ categories }: HeroSectionProps): React.JSX.Element {
  const activeSlug = useCategoryStore((s) => s.activeCategorySlug);
  const setActiveCategory = useCategoryStore((s) => s.setActiveCategory);

  const handleSelectBall = (cat: CategoryItem): void => {
    if (activeSlug === cat.slug) {
      setActiveCategory(null, { y: 0, x: 0 });
    } else {
      setActiveCategory(cat.slug, { y: cat.planetRotY, x: cat.planetRotX });
    }
  };

  return (
    <section className="relative pt-4 sm:pt-6 pb-8 sm:pb-12 w-full overflow-hidden">
      {/* Banner Principal Responsive */}
      <div className="w-full px-3 sm:px-8 lg:px-12 text-center relative z-10 mb-2 sm:mb-4">
        <div className="inline-flex items-center space-x-1.5 px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-2">
          <span>🧸 Tienda Anime • La Rioja Capital</span>
        </div>
        <h1 className="text-3xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white uppercase drop-shadow-[0_0_25px_rgba(76,175,80,0.3)]">
          Planeta Kaio
        </h1>
        <p className="mt-1.5 text-xs sm:text-base text-slate-300 max-w-3xl mx-auto leading-relaxed px-2">
          Toca una de las <span className="text-amber-400 font-bold">7 Esferas</span> para orientar el planeta 3D o ingresa directamente a su catálogo de productos.
        </p>
      </div>

      {/* Escena 3D con altura adaptativa a la pantalla del celular */}
      <div className="w-full relative z-0">
        <PlanetScene />
      </div>

      {/* Selector de las 7 Dragon Balls (Scroll horizontal nativo y táctil en celulares) */}
      <div id="categorias" className="w-full px-3 sm:px-8 lg:px-12 relative z-10 mt-2">
        <div className="flex items-center justify-between mb-3 border-b border-[#2A2545]/60 pb-2">
          <div className="flex items-center space-x-1.5">
            <span className="text-amber-400 font-bold text-xs sm:text-sm">★</span>
            <span className="text-[10px] sm:text-xs font-mono tracking-widest text-slate-300 uppercase font-semibold">
              Las 7 Esferas del Dragón
            </span>
          </div>
          <span className="text-[10px] text-emerald-400 font-mono">
            Deslizá para ver todas →
          </span>
        </div>

        {/* Carrusel táctil fluido en móvil (snap-x scroll) y grid en desktop */}
        <div className="flex sm:grid sm:grid-cols-4 lg:grid-cols-7 gap-2.5 overflow-x-auto pb-3 pt-1 scroll-smooth snap-x snap-mandatory scrollbar-none">
          {categories.map((cat) => {
            const isSelected = activeSlug === cat.slug;
            return (
              <div
                key={cat.id}
                className={`snap-center shrink-0 w-[140px] sm:w-auto flex flex-col items-center justify-between p-2.5 sm:p-3.5 rounded-xl border text-center transition-all duration-200 ${
                  isSelected
                    ? 'bg-amber-500/20 border-amber-400 shadow-[0_0_15px_rgba(255,179,0,0.4)] scale-102'
                    : 'bg-[#12111F]/90 border-[#2A2545] hover:border-slate-500'
                }`}
              >
                {/* Botón para orientar en 3D */}
                <button
                  onClick={() => handleSelectBall(cat)}
                  className="w-full flex flex-col items-center cursor-pointer active:scale-95 transition-transform"
                >
                  <div className="relative w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-gradient-to-tr from-amber-600 via-amber-400 to-yellow-300 flex items-center justify-center shadow-md mb-1.5 border border-amber-300/60">
                    <span className="text-[11px] sm:text-xs font-black text-red-600">
                      {cat.dragonBallStar}★
                    </span>
                  </div>
                  <span className="text-[11px] sm:text-xs font-bold text-white line-clamp-1">
                    {cat.name}
                  </span>
                  <span className="text-[9px] text-slate-400 font-mono mt-0.5">
                    {cat.icon} Rotar 3D
                  </span>
                </button>

                {/* Enlace directo a productos */}
                <Link
                  href={`/categoria/${cat.slug}`}
                  className="mt-2.5 w-full py-1.5 px-2 rounded-lg bg-emerald-500/20 active:bg-emerald-500 text-emerald-300 active:text-black font-mono font-bold text-[9px] uppercase tracking-wider transition-colors border border-emerald-500/30 block text-center"
                >
                  Ver Productos →
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
